import InfoIcon from '@mui/icons-material/Info';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem, TableCell, TableRow, Typography } from '@mui/material';
import { format, differenceInDays } from 'date-fns';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ExtendDurationDialog } from 'src/components/assets/dialogs/ExtendDurationDialog';
import { RequestApprovalDialog } from 'src/components/assets/dialogs/RequestApprovalDialog';
import { StartSubscriptionPeriodDialog } from 'src/components/assets/dialogs/StartSubscriptionPeriodDialog';
import { StatusOverviewDialog } from 'src/components/assets/dialogs/StatusOverviewDialog';
import { TransferSharesDialog } from 'src/components/assets/dialogs/TransferSharesDialog';
import { LaunchpadTable } from 'src/components/mui-overrides/LaunchpadTable';
import { LinkTableCell } from 'src/components/mui-overrides/TableCell';
import { Asset } from 'src/models';
import { TAssetStage } from '@brickwise/asset-validation/dist/schema/enums';
import { Routes } from 'src/router/Routes';
import { AssetStatus } from '../AssetStatus';
import { CopyDialog } from '../dialogs/copyDialog';
import { DeleteAssetDialog } from '../dialogs/deleteAssetDialog';
import { PreorderDialog } from '../dialogs/PreorderDialog';

interface Props {
  headline: string;
  primaryAssets: Asset[];
  reload: () => void;
}

export const PrimaryAssetTable: FC<Props> = ({ headline, primaryAssets, reload }) => {
  const { t } = useTranslation();
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const navigate = useNavigate();

  const renderLiveSince = (asset: Asset) => {
    if (asset.investmentDetails.stage === 'primary' || asset.investmentDetails.stage === 'declined') {
      if (asset.investmentDetails.issueStartDate) {
        const date = new Date(asset.investmentDetails.issueStartDate);
        return format(date, 'dd/MM/yyyy');
      } else {
        return '--';
      }
    } else {
      return 'n/a';
    }
  };

  const renderFundingDays = (asset: Asset) => {
    if (asset.investmentDetails.stage === 'primary') {
      if (asset.investmentDetails.issueEndDate) {
        const endDate = new Date(asset.investmentDetails.issueEndDate);
        const currentDate = new Date();
        const daysLeft = differenceInDays(endDate, currentDate);

        if (daysLeft < 0) {
          return '0 ' + t('dashboard.assets.primarymarket.table.daysleft');
        }
        return daysLeft + ' ' + t('dashboard.assets.primarymarket.table.daysleft');
      } else {
        return '--';
      }
    } else {
      return 'n/a';
    }
  };

  const linkUrl = (asset: Asset) => {
    return Routes.ASSETS.EDIT.replace(':assetId', asset.id);
  };

  return (
    <>
      <StatusOverviewDialog open={showStatusDialog} handleClose={() => setShowStatusDialog(false)} />
      <Typography paddingBottom={'22px'} variant="h6">
        {headline}
      </Typography>
      <LaunchpadTable
        hidePagination={true}
        length={primaryAssets?.length}
        renderHeader={() => {
          return (
            <TableRow>
              <TableCell>{t('dashboard.assets.primarymarket.table.name')}</TableCell>
              <TableCell>{t('dashboard.assets.primarymarket.table.pricepershare')}</TableCell>
              <TableCell>{t('dashboard.assets.primarymarket.table.availableshares')}</TableCell>
              <TableCell>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ paddingRight: 2 }}>{t('dashboard.assets.primarymarket.table.status')}</div>
                  <InfoIcon
                    fontSize="small"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setShowStatusDialog(true);
                    }}
                  />
                </div>
              </TableCell>
              <TableCell>{t('dashboard.assets.primarymarket.table.livesince')}</TableCell>
              <TableCell>{t('dashboard.assets.primarymarket.table.fundingdays')}</TableCell>
              <TableCell width={50}>{t('dashboard.assets.primarymarket.table.action')}</TableCell>
            </TableRow>
          );
        }}
        renderRows={() => {
          return (
            <>
              {primaryAssets.map((row, index) => (
                <TableRow key={index} hover style={{ cursor: 'pointer', textDecoration: 'none' }} onClick={() => navigate(linkUrl(row))}>
                  <LinkTableCell to={linkUrl(row)}>
                    <div style={{ fontWeight: 'bold' }}>{row.descriptions?.title || t('dashboard.assets.preparing')}</div>
                    <>
                      {(row.location?.city || row.location?.countryCode) && (
                        <div>
                          {row.location?.city ?? t('dashboard.assets.preparing')},{' '}
                          {row.location?.countryCode ?? t('dashboard.assets.preparing')}
                        </div>
                      )}
                    </>
                  </LinkTableCell>
                  <LinkTableCell to={linkUrl(row)}>
                    {row.investmentDetails.sharePrice ? '€ ' + row.investmentDetails.sharePrice : '--'}
                  </LinkTableCell>
                  <LinkTableCell to={linkUrl(row)}>
                    <>
                      {(row.investmentDetails.availableBricks || '-').toString()} /{row.investmentDetails.totalBricks || '-'}
                    </>
                  </LinkTableCell>
                  <LinkTableCell to={linkUrl(row)}>{<AssetStatus status={row.investmentDetails.stage} />}</LinkTableCell>
                  <LinkTableCell to={linkUrl(row)}>{renderLiveSince(row)}</LinkTableCell>
                  <LinkTableCell to={linkUrl(row)}>{renderFundingDays(row)}</LinkTableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {row.investmentDetails.stage !== 'review' ? <PrimaryAssetMenu asset={row} reload={reload} /> : null}
                  </TableCell>
                </TableRow>
              ))}
            </>
          );
        }}
      />
    </>
  );
};

interface PrimaryAssetMenuProps {
  asset: Asset;
  reload: () => void;
}

export const PrimaryAssetMenu: FC<PrimaryAssetMenuProps> = ({ asset, reload }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [requestApprovalDialogOpen, setRequestApprovalDialogOpen] = useState(false);
  const [preorderDialogOpen, setPreorderDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [deleteAssetDialogOpen, setDeleteAssetDialogOpen] = useState(false);
  const [startSubscriptionPeriodDialogOpen, setStartSubscriptionPeriodDialogOpen] = useState(false);
  const [extendDurationDialogOpen, setExtendDurationDialogOpen] = useState(false);
  const [transerShareDialogOpen, setTranserShareDialogOpen] = useState(false);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (e: any) => {
    e.stopPropagation();
    setAnchorEl(null);
  };
  return (
    <>
      <RequestApprovalDialog
        open={requestApprovalDialogOpen}
        asset={asset}
        handleClose={() => setRequestApprovalDialogOpen(false)}
        triggerRefresh={reload}
      />

      <PreorderDialog open={preorderDialogOpen} asset={asset} handleClose={() => setPreorderDialogOpen(false)} triggerRefresh={reload} />

      <CopyDialog open={copyDialogOpen} assetId={asset.id} handleClose={() => setCopyDialogOpen(false)} triggerRefresh={reload} />

      <DeleteAssetDialog
        title={asset.descriptions.title}
        open={deleteAssetDialogOpen}
        assetId={asset.id}
        handleClose={() => setDeleteAssetDialogOpen(false)}
        triggerRefresh={reload}
      />

      <ExtendDurationDialog
        open={extendDurationDialogOpen}
        asset={asset}
        handleClose={() => setExtendDurationDialogOpen(false)}
        triggerRefresh={reload}
      />
      <TransferSharesDialog
        open={transerShareDialogOpen}
        handleClose={() => setTranserShareDialogOpen(false)}
        asset={asset}
        triggerRefresh={reload}
      />
      <StartSubscriptionPeriodDialog
        open={startSubscriptionPeriodDialogOpen}
        handleClose={() => setStartSubscriptionPeriodDialogOpen(false)}
        asset={asset}
        triggerRefresh={reload}
      />
      <IconButton
        onClick={(event) => {
          event.stopPropagation();
          handleClick(event);
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {(asset.investmentDetails.stage === TAssetStage.preparing ||
          asset.investmentDetails.stage === TAssetStage.preorder ||
          asset.investmentDetails.stage === TAssetStage.declined) && (
          <MenuItem
            onClick={(event) => {
              setRequestApprovalDialogOpen(true);
              handleClose(event);
            }}
          >
            {t('dashboard.assets.primarymarket.table.menu.requestapproval')}
          </MenuItem>
        )}

        {asset.investmentDetails.stage === TAssetStage.preparing && (
          <MenuItem
            onClick={(event) => {
              setPreorderDialogOpen(true);
              handleClose(event);
            }}
          >
            {t('dashboard.assets.primarymarket.table.menu.startpreorder')}
          </MenuItem>
        )}

        <MenuItem
          onClick={(event) => {
            setCopyDialogOpen(true);
            handleClose(event);
          }}
        >
          {t('dashboard.assets.dialog.copy.confirm')}
        </MenuItem>

        {asset.investmentDetails.stage === TAssetStage.approved ? (
          <MenuItem
            onClick={(event) => {
              handleClose(event);
              setStartSubscriptionPeriodDialogOpen(true);
            }}
          >
            {t('dashboard.assets.primarymarket.table.menu.startsubscriptionperiod')}
          </MenuItem>
        ) : null}

        {asset.investmentDetails.stage === TAssetStage.primary ? (
          <MenuItem
            onClick={(event) => {
              handleClose(event);
              setExtendDurationDialogOpen(true);
            }}
          >
            {t('dashboard.assets.primarymarket.table.menu.extendduration')}
          </MenuItem>
        ) : null}
        {asset.investmentDetails.stage === TAssetStage.primary ? (
          <MenuItem
            onClick={(event) => {
              handleClose(event);
              setTranserShareDialogOpen(true);
            }}
          >
            {t('dashboard.assets.primarymarket.table.menu.transfershares')}
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={(event) => {
            handleClose(event);
            setDeleteAssetDialogOpen(true);
          }}
          disabled={asset.investmentDetails.stage !== TAssetStage.preparing && asset.investmentDetails.stage !== TAssetStage.preorder}
        >
          {t('dashboard.assets.primarymarket.table.menu.delete')}
        </MenuItem>
      </Menu>
    </>
  );
};
